// =============================================================================
// SIREPPAR - Cliente API
// =============================================================================

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';

// =============================================================================
// Configuración
// =============================================================================

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

/**
 * URL de descarga de la planilla del padrón de afiliados. Sale del backend (que la
 * sirve desde docs/, el MISMO archivo que se adjunta al correo del reconocimiento
 * provisorio), así que hay un solo archivo y una sola versión. Antes cada pantalla
 * apuntaba a una copia distinta en S3, que quedó desactualizada.
 * El endpoint es público: la pantalla de login la ofrece sin sesión.
 */
export const URL_PLANTILLA_AFILIADOS = `${API_URL}/afiliaciones/plantilla-publica`;

// =============================================================================
// Crear instancia de Axios
// =============================================================================

export const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// =============================================================================
// Interceptor de solicitudes
// =============================================================================

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// =============================================================================
// Interceptor de respuestas
// =============================================================================

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // Error de red
    if (!error.response) {
      toast.error('Error de conexión. Verifique su conexión a internet.');
      return Promise.reject(error);
    }

    const { status, data } = error.response as { status: number; data: any };

    // Token expirado - intentar refresh
    if (status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/refresh')) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await api.post('/auth/refresh');
        const { accessToken } = response.data.data;
        
        localStorage.setItem('accessToken', accessToken);
        
        processQueue(null, accessToken);
        
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        
        localStorage.removeItem('accessToken');
        window.location.href = '/login';
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Manejar errores específicos
    if (status === 403) {
      toast.error('No tiene permisos para realizar esta acción');
    } else if (status === 404) {
      toast.error('Recurso no encontrado');
    } else if (status === 429) {
      toast.error('Demasiadas solicitudes. Intente más tarde.');
    } else if (status >= 500) {
      toast.error('Error del servidor. Intente más tarde.');
    } else if (data?.error?.message) {
      toast.error(data.error.message);
    }

    return Promise.reject(error);
  }
);

// =============================================================================
// Servicios específicos
// =============================================================================

export const authService = {
  login: (email: string, password: string, mfaCode?: string) =>
    api.post('/auth/login', { email, password, mfaCode }),
  
  logout: () => api.post('/auth/logout'),
  
  refresh: () => api.post('/auth/refresh'),
  
  me: () => api.get('/auth/me'),
  
  setupMFA: () => api.post('/auth/mfa/setup'),
  
  verifyMFA: (code: string) => api.post('/auth/mfa/verify', { code }),
  
  disableMFA: (code: string) => api.post('/auth/mfa/disable', { code }),
};

export const partidoService = {
  list: (params?: Record<string, string | number>) =>
    api.get('/partidos', { params }),

  // incluirInactivos: suma caducos/extinguidos/fusionados/absorbidos. La vista de
  // /partidos los necesita (es el listado de partidos); las métricas, no.
  getCumplimiento: (incluirInactivos = false) =>
    api.get('/partidos/cumplimiento', {
      params: incluirInactivos ? { incluirInactivos: 'true' } : undefined,
    }),

  getEstadoDocumentacion: () =>
    api.get('/partidos/estado-documentacion'),

  // Nota interna del partido (columna "Nota" de /partidos).
  updateNota: (id: string, nota: string | null) =>
    api.patch(`/partidos/${id}/nota`, { nota }),

  // PDF del listado de /partidos. Se mandan las filas ya filtradas y ordenadas
  // para que el PDF diga exactamente lo que se ve en pantalla.
  descargarListadoPdf: async (payload: { filas: any[]; filtros?: Record<string, string | null> }) => {
    const response = await api.post('/partidos/listado-pdf', payload, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `Listado-Partidos-${new Date().toISOString().slice(0, 10)}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  },

  // Descarga la Hoja de Inspección (.docx) del partido y dispara la descarga.
  descargarHojaInspeccion: async (id: string, sigla?: string) => {
    const response = await api.get(`/partidos/${id}/hoja-inspeccion`, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `Hoja-Inspeccion-${sigla || id}.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  },

  getById: (id: string, include?: string) =>
    api.get(`/partidos/${id}`, { params: { include } }),
  
  getByNumero: (numero: number) =>
    api.get(`/partidos/numero/${numero}`),
  
  create: (data: any) =>
    api.post('/partidos', data),
  
  update: (id: string, data: any) =>
    api.put(`/partidos/${id}`, data),
  
  getAutoridades: (id: string) =>
    api.get(`/partidos/${id}/autoridades`),
  
  setAutoridades: (id: string, autoridades: any[]) =>
    api.put(`/partidos/${id}/autoridades`, { autoridades }),
  
  updateAutoridades: (id: string, autoridades: any[]) =>
    api.put(`/partidos/${id}/autoridades`, { autoridades }),

  getMandatoAutoridad: (partidoId: string, autoridadId: string) =>
    api.get(`/partidos/${partidoId}/autoridades/${autoridadId}/mandato`),

  editarAutoridad: (partidoId: string, autoridadId: string, data: any) =>
    api.patch(`/partidos/${partidoId}/autoridades/${autoridadId}`, data),

  renovarMandatoAutoridad: (partidoId: string, autoridadId: string, data: any) =>
    api.post(`/partidos/${partidoId}/autoridades/${autoridadId}/mandato/renovar`, data),

  bajarAutoridad: (partidoId: string, autoridadId: string) =>
    api.put(`/partidos/${partidoId}/autoridades/${autoridadId}/bajar`),

  getOrganos: (id: string) =>
    api.get(`/partidos/${id}/organos`),

  crearOrgano: (id: string, data: any) =>
    api.post(`/partidos/${id}/organos`, data),

  agregarAutoridadOrgano: (partidoId: string, organoId: string, data: any) =>
    api.post(`/partidos/${partidoId}/organos/${organoId}/autoridades`, data),

  bajarOrgano: (partidoId: string, organoId: string) =>
    api.patch(`/partidos/${partidoId}/organos/${organoId}/baja`),

  renovarMandatoOrgano: (partidoId: string, organoId: string, data: any) =>
    api.put(`/partidos/${partidoId}/organos/${organoId}/renovar-mandato`, data),

  // `expedienteId` amplía la búsqueda de archivos a los que se cargaron contra el
  // EXPEDIENTE y no contra el partido (wizard, respuesta a una observación). Sin él
  // el partido veía menos archivos que el lado interno.
  getAutoridadesDni: (id: string, expedienteId?: string) =>
    api.get(`/partidos/${id}/autoridades-dni`, { params: expedienteId ? { expedienteId } : undefined }),

  getApoderadosDni: (id: string, expedienteId?: string) =>
    api.get(`/partidos/${id}/apoderados-dni`, { params: expedienteId ? { expedienteId } : undefined }),

  getIniciadorDni: (id: string) =>
    api.get(`/partidos/${id}/iniciador-dni`),

  createApoderado: (id: string, data: any) =>
    api.post(`/partidos/${id}/apoderados`, data),

  editarApoderado: (partidoId: string, apoderadoId: string, data: any) =>
    api.patch(`/partidos/${partidoId}/apoderados/${apoderadoId}`, data),

  bajarApoderado: (partidoId: string, apoderadoId: string) =>
    api.put(`/partidos/${partidoId}/apoderados/${apoderadoId}/bajar`),

  buscarMiembro: (id: string, dni: string) =>
    api.get(`/partidos/${id}/miembro/${dni}`),

  verificarRequisitos: (id: string) =>
    api.get(`/partidos/${id}/verificar-requisitos`),
  
  cambiarEstado: (id: string, data: { estado: string; motivo: string; resolucion?: string }) =>
    api.post(`/partidos/${id}/cambiar-estado`, data),
    
  getEstadisticas: () =>
    api.get('/partidos/stats'),
};

/**
 * Secciones de documentación de un expediente (`expediente_tipos_documento`).
 *
 * Vivían en `alianzaGestionService` porque la aprobación por tipo arrancó como
 * piloto de alianzas. Dejó de serlo: también la usan reempadronamiento y
 * reconocimiento, así que cuelgan del expediente, que es de quien son.
 */
/**
 * El check por observación: darla por subsanada, y destildarla.
 *
 * Vivía en `alianzaGestionService` y el backend rechazaba cualquier expediente que
 * no fuera de alianza. Ya no: en los demás trámites cerrarlas de golpe con "Aceptar
 * subsanación" no sirve cuando una está subsanada y otra no.
 */
export const observacionCheckService = {
  resolver: (observacionId: string, comentario?: string) =>
    api.post(`/observaciones/${observacionId}/resolver`, { comentario }),

  /** Destilda el check: la observación vuelve a quedar abierta. */
  reabrir: (observacionId: string) =>
    api.post(`/observaciones/${observacionId}/reabrir`),
};

export const seccionesDocumentoService = {
  listar: (expedienteId: string) =>
    api.get(`/expedientes/${expedienteId}/tipos-documento`).then(r => r.data.data),

  /** `dimension` decide QUÉ columna se escribe: la sección tiene las dos, igual que
   *  el documento. Sin pasarla el backend asume ANALISIS. */
  aprobar: (
    expedienteId: string, tipo: string, estado: string,
    nota?: string, dimension: 'ADMISIBILIDAD' | 'ANALISIS' = 'ANALISIS',
  ) =>
    api.patch(`/expedientes/${expedienteId}/tipos-documento/${tipo}`, { estado, nota, dimension }),

  /** Sólo la nota de la sección, sin tocar ninguna de las dos aprobaciones. */
  nota: (expedienteId: string, tipo: string, nota: string) =>
    api.patch(`/expedientes/${expedienteId}/tipos-documento/${tipo}`, { nota }),

  eximir: (expedienteId: string, tipo: string, obligatorio: boolean) =>
    api.patch(`/expedientes/${expedienteId}/tipos-documento/${tipo}/obligatorio`, { obligatorio }),

  /**
   * "Aprobar todo Admisibilidad": cierra la dimensión de admisibilidad completa
   * —archivos y check de cada tipo— sin mover la etapa del expediente.
   *
   * Sólo toca lo que está en PENDIENTE: no puede deshacer un rechazo. Devuelve
   * cuántos archivos y cuántas secciones cambió.
   */
  aprobarTodoAdmisibilidad: (expedienteId: string) =>
    api.post(`/admisibilidad/${expedienteId}/aprobar-todo`)
      .then(r => r.data.data as { secciones: number; documentos: number }),
};

const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

export const expedienteService = {
  list: (params?: Record<string, any>) =>
    api.get('/expedientes', { params }),
  
  getById: (id: string, params?: Record<string, any>) =>
    api.get(`/expedientes/${id}`, { params }),
  
  create: (data: any) =>
    api.post('/expedientes', data),
  
  update: (id: string, data: any) =>
    api.put(`/expedientes/${id}`, data),
  
  updateDatos: (id: string, datosFormulario: any) =>
    api.put(`/expedientes/${id}/datos`, { datosFormulario }),
  
  presentar: (id: string) =>
    api.post(`/expedientes/${id}/presentar`),
  
  asignar: (id: string, analistaId: string) =>
    api.post(`/expedientes/${id}/asignar`, { analistaId }),
  
  observar: (id: string, formData: FormData) =>
    api.post(`/expedientes/${id}/observar`, formData, { ...MULTIPART, timeout: 300000 }),

  urlAdjuntoObservacion: (id: string, observacionId: string, adjuntoId: string) =>
    api.get(`/expedientes/${id}/observaciones/${observacionId}/adjuntos/${adjuntoId}/url`)
      .then(r => r.data.data.url as string),
  
  aprobar: (id: string, data: { resolucionNumero: string; resolucionTexto: string }) =>
    api.post(`/expedientes/${id}/aprobar`, data),
  
  rechazar: (id: string, data: { motivo: string; resolucionNumero: string }) =>
    api.post(`/expedientes/${id}/rechazar`, data),
  
  archivar: (id: string, body?: { forzar?: boolean; motivo?: string }) =>
    api.post(`/expedientes/${id}/archivar`, body ?? {}),
  
  getMovimientos: (id: string) =>
    api.get(`/expedientes/${id}/movimientos`),

  getDocumentosResumen: (id: string) =>
    api.get(`/expedientes/${id}/documentos-resumen`),

  getAutoridadesDni: (id: string) =>
    api.get(`/expedientes/${id}/autoridades-dni`),

  subsanarObservacion: (id: string, observacionId: string) =>
    api.post(`/expedientes/${id}/observaciones/${observacionId}/subsanar`),
  
  generarConstancia: (id: string) =>
    api.get(`/expedientes/${id}/constancia`),
  
  bandeja: () =>
    api.get('/expedientes/bandeja'),
  
  estadisticas: () =>
    api.get('/expedientes/estadisticas'),
};

export const usuarioService = {
  /**
   * Da acceso a la agrupación ACTIVA a alguien que ya tiene usuario. No crea cuenta:
   * otorga membresía en `usuarios_agrupaciones`. La agrupación la resuelve el
   * backend con el contexto de la sesión.
   */
  vincularAgrupacion: (data: { dni: string; rol: string }) =>
    api.post('/usuarios/vincular-agrupacion', data),

    list: (params?: Record<string, any>) =>
      api.get('/usuarios', { params }),
    getById: (id: string) =>
      api.get(`/usuarios/${id}`),
    create: (data: any) =>
      api.post('/usuarios', data),
    update: (id: string, data: any) =>
      api.put(`/usuarios/${id}`, data),
    resetPassword: (id: string, newPassword: string) =>
      api.post(`/usuarios/${id}/reset-password`, { newPassword }),
    unlock: (id: string) =>
      api.post(`/usuarios/${id}/desbloquear`),
    getSessions: (id: string) =>
      api.get(`/usuarios/${id}/sesiones`),
    disableMFA: (id: string) =>
      api.post(`/usuarios/${id}/disable-mfa`),
    revokeSessions: (id: string) =>
      api.post(`/usuarios/${id}/revocar-sesiones`),
  };

export const padronService = {
  validarDni: (dni: string) =>
    api.get(`/padrones/validar-dni/${dni}`),
};

export const convocatoriaService = {
  getActual: () =>
    api.get('/candidaturas/convocatorias/actual'),

  getAgrupaciones: (convocatoriaId: string) =>
    api.get(`/candidaturas/convocatorias/${convocatoriaId}/agrupaciones`),

  actualizarHabilitacion: (convocatoriaId: string, partidoId: string, data: { habilitada: boolean; motivoDeshabilitacion?: string }) =>
    api.patch(`/candidaturas/convocatorias/${convocatoriaId}/agrupaciones/${partidoId}`, data),

  getMiEstado: () =>
    api.get('/candidaturas/mi-estado'),

  getDisponibles: (convocatoriaId: string, busqueda?: string) =>
    api.get(`/candidaturas/convocatorias/${convocatoriaId}/agrupaciones-disponibles`, {
      params: busqueda ? { busqueda } : undefined,
    }),

  agregarAgrupacion: (convocatoriaId: string, partidoId: string) =>
    api.post(`/candidaturas/convocatorias/${convocatoriaId}/agrupaciones`, { partidoId }),
};

export const candidatoService = {
  crearBorrador: (convocatoriaId: string, partidoId: string) =>
    api.post('/candidaturas/candidatos/borrador', { convocatoriaId, partidoId }),

  obtener: (candidatoId: string) =>
    api.get(`/candidaturas/candidatos/${candidatoId}`),

  actualizar: (candidatoId: string, cambios: Record<string, any>) =>
    api.patch(`/candidaturas/candidatos/${candidatoId}`, cambios),

  subirDocumento: (candidatoId: string, tipo: string, archivo: File) => {
    const formData = new FormData();
    formData.append('archivo', archivo);
    formData.append('tipo', tipo);
    return api.post(`/candidaturas/candidatos/${candidatoId}/documentos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  verificarDni: (dni: string) =>
    api.get(`/candidaturas/padron/verificar/${dni}`),

  reemplazarDocumento: (documentoId: string, archivo: File) => {
    const formData = new FormData();
    formData.append('archivo', archivo);
    return api.patch(`/candidaturas/documentos/${documentoId}/reemplazar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  eliminarDocumento: (documentoId: string) =>
    api.delete(`/candidaturas/documentos/${documentoId}`),

  guardarDeclaracionJurada: (candidatoId: string, datos: { incisos: Record<string, boolean>; candidaturaUnica: boolean; declaracionFinal: boolean }) =>
    api.post(`/candidaturas/candidatos/${candidatoId}/declaracion-jurada`, datos),
 
  obtenerPorExpediente: (expedienteId: string) =>
    api.get(`/candidaturas/candidatos/por-expediente/${expedienteId}`),

  obtenerObservaciones: (candidatoId: string) =>
    api.get(`/candidaturas/candidatos/${candidatoId}/observaciones`),

  obtenerValidacion: (candidatoId: string) =>
    api.get(`/candidaturas/candidatos/${candidatoId}/validacion`),

  enviar: (candidatoId: string) =>
    api.post(`/candidaturas/candidatos/${candidatoId}/enviar`),

  descargarPdf: (candidatoId: string) =>
    api.get(`/candidaturas/candidatos/${candidatoId}/pdf`, { responseType: 'blob' }),

  descargarModeloAceptacion: () =>
    api.get('/candidaturas/modelos/aceptacion-candidatura', { responseType: 'blob' }),
};
export const candidaturaExpedienteService = {
  listar: (params: Record<string, string | number>) =>
    api.get('/candidaturas/expedientes', { params }),

  contadores: (convocatoriaId?: string) =>
    api.get('/candidaturas/expedientes/contadores', { params: convocatoriaId ? { convocatoriaId } : undefined }),

  obtener: (id: string) =>
    api.get(`/candidaturas/expedientes/${id}`),

  timeline: (id: string) =>
    api.get(`/candidaturas/expedientes/${id}/timeline`),

  asignar: (id: string, analistaId: string) =>
    api.post(`/candidaturas/expedientes/${id}/asignar`, { analistaId }),

  iniciarAnalisis: (id: string) =>
    api.post(`/candidaturas/expedientes/${id}/iniciar-analisis`),

  elevarSecretario: (id: string) =>
    api.post(`/candidaturas/expedientes/${id}/elevar-secretario`),

  crearObservacion: (id: string, formData: FormData) =>
    api.post(`/candidaturas/expedientes/${id}/observaciones`, formData, { ...MULTIPART, timeout: 300000 }),

  resolverObservacion: (observacionId: string, datos: { estado: 'RESUELTA' | 'RECHAZADA'; motivo?: string }) =>
    api.post(`/candidaturas/observaciones/${observacionId}/resolver`, datos),

  reabrirObservacion: (observacionId: string) =>
    api.post(`/candidaturas/observaciones/${observacionId}/reabrir`),

  inhabilidades: (id: string) =>
    api.get(`/candidaturas/expedientes/${id}/inhabilidades`),

  informeTecnico: (id: string, datos: { observaciones: string; recomendacion: 'OFICIALIZAR' | 'RECHAZAR' | 'OFICIALIZAR_CON_OBSERVACIONES'; persistir?: boolean }) =>
    api.post(`/candidaturas/expedientes/${id}/informe-tecnico`, datos),

  borradorElevarSecretario: (id: string) =>
    api.get(`/candidaturas/expedientes/${id}/elevar-secretario/borrador`).then(r => r.data.data.texto),

  cruzarExcel: (tipo: 'RAP' | 'RAM', archivo: File, expedienteId?: string) => {
    const fd = new FormData();
    fd.append('archivo', archivo);
    if (expedienteId) fd.append('expedienteId', expedienteId);
    return api.post(`/candidaturas/gaiman/cruzar-${tipo.toLowerCase()}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  actualizarInciso: (id: string, inciso: string, datos: { estado: string; comentario?: string; documentoId?: string }) =>
    api.patch(`/candidaturas/expedientes/${id}/inhabilidades/${inciso}`, datos),

  guardarBorrador: (id: string, texto: string) =>
    api.post(`/expedientes/${id}/borrador-observacion`, { texto }),

  obtenerBorrador: (id: string) =>
    api.get(`/expedientes/${id}/borrador-observacion`),
};

// =============================================================================
// Alianzas (arts. 26-27) — wizard público de constitución
// =============================================================================
// Todos los endpoints son públicos mientras el borrador está abierto: el usuario
// del apoderado iniciador se crea recién al enviar. Las mutaciones devuelven la
// alianza completa, así que el wizard no necesita refetch después de cada cambio.
export interface AlianzaArchivoSubido {
  path: string;
  nombreOriginal: string;
  mimeType?: string;
}

export const alianzaService = {
  convocatoriasAbiertas: () =>
    api.get('/alianzas/convocatorias-abiertas'),

  partidosElegibles: (convocatoriaId: string) =>
    api.get(`/alianzas/partidos-elegibles/${convocatoriaId}`),

  /** ¿El correo se puede usar como cuenta de acceso de esa persona? */
  /** ¿Esa persona ya tiene usuario? Devuelve el correo enmascarado, nunca completo. */
  accesoPorDni: (dni: string) =>
    api.get(`/alianzas/acceso-por-dni/${encodeURIComponent(dni)}`),
  correoDisponible: (email: string, dni?: string) =>
    api.get(`/alianzas/correo-disponible/${encodeURIComponent(email.trim().toLowerCase())}`, {
      params: dni ? { dni: dni.replace(/\D/g, '') } : undefined,
    }),

  crearBorrador: (convocatoriaId: string) =>
    api.post('/alianzas/borrador', { convocatoriaId }),

  obtener: (id: string) =>
    api.get(`/alianzas/${id}`),

  actualizar: (id: string, cambios: Record<string, unknown>) =>
    api.patch(`/alianzas/${id}`, cambios),

  agregarIntegrantePartido: (id: string, partidoId: string) =>
    api.post(`/alianzas/${id}/integrantes`, { partidoId }),

  agregarIntegranteOtros: (id: string, datos: { miembroNoMigradoDenominacion: string; miembroNoMigradoTipo: string; miembroNoMigradoLocalidadId?: number }) =>
    api.post(`/alianzas/${id}/integrantes`, datos),

  quitarIntegrante: (id: string, integranteId: string) =>
    api.delete(`/alianzas/${id}/integrantes/${integranteId}`),

  cargarActaVoluntad: (id: string, integranteId: string, archivo: AlianzaArchivoSubido) =>
    api.post(`/alianzas/${id}/integrantes/${integranteId}/acta`, archivo),

  subirDocumento: (id: string, tipo: string, archivo: AlianzaArchivoSubido) =>
    api.post(`/alianzas/${id}/documentos`, { tipo, ...archivo }),

  quitarDocumento: (id: string, documentoId: string) =>
    api.delete(`/alianzas/${id}/documentos/${documentoId}`),

  agregarApoderado: (id: string, datos: Record<string, unknown>) =>
    api.post(`/alianzas/${id}/apoderados`, datos),

  actualizarApoderado: (apoderadoId: string, cambios: Record<string, unknown>) =>
    api.patch(`/alianzas/apoderados/${apoderadoId}`, cambios),

  quitarApoderado: (apoderadoId: string) =>
    api.delete(`/alianzas/apoderados/${apoderadoId}`),

  designarApoderadoDeAcceso: (apoderadoId: string) =>
    api.patch(`/alianzas/apoderados/${apoderadoId}/acceso`),

  cargarActaApoderado: (apoderadoId: string, archivo: AlianzaArchivoSubido) =>
    api.post(`/alianzas/apoderados/${apoderadoId}/acta`, archivo),

  cargarDniApoderado: (apoderadoId: string, cara: 'frente' | 'dorso', archivo: AlianzaArchivoSubido) =>
    api.post(`/alianzas/apoderados/${apoderadoId}/dni-${cara}`, archivo),

  crearOrgano: (id: string, denominacion: string) =>
    api.post(`/alianzas/${id}/organos`, { denominacion }),

  renombrarOrgano: (organoId: string, denominacion: string) =>
    api.patch(`/alianzas/organos/${organoId}`, { denominacion }),

  eliminarOrgano: (organoId: string) =>
    api.delete(`/alianzas/organos/${organoId}`),

  agregarAutoridadOrgano: (organoId: string, datos: Record<string, unknown>) =>
    api.post(`/alianzas/organos/${organoId}/autoridades`, datos),

  actualizarAutoridadOrgano: (autoridadId: string, cambios: Record<string, unknown>) =>
    api.patch(`/alianzas/organos/autoridades/${autoridadId}`, cambios),

  quitarAutoridadOrgano: (autoridadId: string) =>
    api.delete(`/alianzas/organos/autoridades/${autoridadId}`),

  cargarDniAutoridad: (autoridadId: string, cara: 'frente' | 'dorso', archivo: AlianzaArchivoSubido) =>
    api.post(`/alianzas/organos/autoridades/${autoridadId}/dni-${cara}`, archivo),

  cargarActaOrgano: (organoId: string, archivo: AlianzaArchivoSubido) =>
    api.post(`/alianzas/organos/${organoId}/acta`, archivo),

  validaciones: (id: string) =>
    api.get(`/alianzas/${id}/validaciones`),

  enviar: (id: string, credenciales?: { email: string; password: string; passwordConfirmacion: string }) =>
    api.post(`/alianzas/${id}/enviar`, { credenciales, aceptaDeclaracion: true }),

  urlPdf: (id: string) => `${API_URL}/alianzas/${id}/pdf`,
};

export const documentoService = {
  list: (params?: Record<string, string | number>) =>
    api.get('/documentos', { params }),
  
  getById: (id: string) =>
    api.get(`/documentos/${id}`),
  
  upload: (formData: FormData) =>
    api.post('/documentos', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  checkDniPersona: (dni: string) =>
    api.get(`/documentos/check-dni-persona/${dni.replace(/\D/g, '')}`),

  vincularExistente: (data: any) =>
    api.post('/documentos/vincular-existente', data),

  // Renombrar y/o mover de sección (cambiar el tipo). El backend decide qué
  // puede tocar cada rol: interno = nombre + tipo, partido = solo el nombre.
  updateMetadata: (id: string, data: { titulo?: string; tipo?: string }) =>
    api.patch(`/documentos/${id}`, data),

  getUrl: (id: string) =>
    api.get(`/documentos/${id}/url`),

  download: async (id: string) => {
    const response = await api.get(`/documentos/${id}/url`);
    const url = response.data.data?.url || response.data.url;
    if (url) {
      window.open(url, '_blank');
    }
  },

  forceDownload: async (id: string, filename?: string) => {
    const response = await api.get(`/documentos/${id}/url`);
    const url = response.data.data?.url || response.data.url;
    if (url) {
      // Fetch como blob para forzar descarga (los presigned URLs de S3 no respetan
      // el atributo download en cross-origin si se pone directo en <a>)
      const blob = await fetch(url).then(r => r.blob());
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'documento';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    }
  },
  
  // Descarga TODOS los documentos del expediente en un ZIP, ya ordenados en
  // carpetas por sección. No hay límite de tamaño: el backend lo manda por stream
  // mientras lo comprime. Acá se usa el patrón blob del listado de partidos y la
  // hoja de inspección, con la salvedad de que el navegador no puede mostrar
  // progreso (la respuesta viene sin Content-Length, ver el controlador).
  descargarZipExpediente: async (expedienteId: string) => {
    let response;
    try {
      response = await api.get(`/documentos/expediente/${expedienteId}/zip`, { responseType: 'blob', timeout: 300000 });
    } catch (error: any) {
      // Con responseType 'blob' el cuerpo del error TAMBIÉN llega como Blob, así que
      // el interceptor no puede leer `data.error.message` y el usuario se quedaba sin
      // saber qué pasó. Hay que desenvolverlo a mano.
      const cuerpo = error?.response?.data;
      if (cuerpo instanceof Blob) {
        try {
          const json = JSON.parse(await cuerpo.text());
          throw new Error(json?.error?.message || json?.message || 'No se pudo generar la descarga.');
        } catch (e: any) {
          if (e instanceof Error && e.message) throw e;
        }
      }
      throw new Error(cuerpo?.error?.message || 'No se pudo generar la descarga.');
    }

    // El nombre real ("<Nº expediente> - <denominación>.zip") viaja en la cabecera,
    // en la forma extendida de RFC 5987 porque lleva tildes.
    const disposicion = (response.headers?.['content-disposition'] as string) || '';
    const extendido = /filename\*=UTF-8''([^;]+)/i.exec(disposicion)?.[1];
    const simple = /filename="([^"]+)"/i.exec(disposicion)?.[1];
    let nombre = 'documentos-expediente.zip';
    if (extendido) {
      try { nombre = decodeURIComponent(extendido); } catch { nombre = extendido; }
    } else if (simple) {
      nombre = simple;
    }

    const blobUrl = URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = nombre;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  },

  createVersion: (id: string, formData: FormData) =>
    api.post(`/documentos/${id}/version`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  
  delete: (id: string) =>
    api.delete(`/documentos/${id}`),

  updateEstadoRevision: (
    id: string,
    estado: 'PENDIENTE' | 'APROBADO' | 'RECHAZADO',
    observacion?: string | null,
    etapa: 'ADMISIBILIDAD' | 'ANALISIS' = 'ADMISIBILIDAD',
  ) =>
    api.patch(`/documentos/${id}/estado-revision`, { estadoRevision: estado, observacion, etapa }),

  // Nota de la Secretaría sobre el documento (columna "Nota" del tablero de
  // estado de la documentación). Cadena vacía = borrar la nota.
  updateNota: (id: string, nota: string | null) =>
    api.patch(`/documentos/${id}/nota`, { nota }),

  // Corrige el motivo de una revisión SIN cambiar el estado ni disparar las
  // cascadas entre dimensiones. Cadena vacía = borrar el motivo (el backend lo
  // rechaza si el documento está RECHAZADO: un rechazo no puede quedar sin motivo).
  updateObservacion: (id: string, etapa: 'ADMISIBILIDAD' | 'ANALISIS', observacion: string | null) =>
    api.patch(`/documentos/${id}/observacion`, { etapa, observacion }),

  // Verifica el documento LISTADO_ADHERENTES_EXCEL disparando el control de adherentes.
  // El backend baja el Excel, lo carga y decide APROBADO/RECHAZADO según el mínimo legal.
  verificarAdherentes: (documentoId: string) =>
    api.post(`/adherentes/documento/${documentoId}/verificar`, {}, { timeout: 300000 }),
};

// =============================================================================
// Flujo de RECONOCIMIENTO (revisión de atributos + documentación)
// =============================================================================

export const reconocimientoService = {
  cola: () => api.get('/reconocimiento/cola'),

  /**
   * Ejecuta una transición de fase del expediente de reconocimiento.
   * @param accion sufijo del endpoint (p.ej. 'observar-atributos', 'aprobar-documentacion')
   * @param body  { texto } | { motivo, atributosRechazados? } | { comentario } según la acción
   */
  accion: (id: string, accion: string, body?: Record<string, unknown>) =>
    api.post(`/reconocimiento/${id}/${accion}`, body ?? {}),

  observar: (id: string, formData: FormData) =>
    api.post(`/reconocimiento/${id}/observar`, formData, { ...MULTIPART, timeout: 300000 }),

  /**
   * Carga (por el apoderado, vía wizard) los documentos del Art. 18 + los
   * órganos y autoridades promotoras. Solo válido en fase DOCUMENTACION_PENDIENTE.
   */
  cargarDocumentacion: (id: string, body: {
    organos: Array<{
      tipoOrgano: string;
      denominacion?: string;
      denominacionPersonalizada?: string | null;
      esObligatorio?: boolean;
      promotores: Array<{ dni: string; nombre: string; apellido: string; genero?: string; cargo?: string | null; dniFrentePath?: string | null; dniDorsoPath?: string | null }>;
    }>;
    apoderados: Array<{ dni: string; nombre: string; apellido: string; genero?: string; dniFrentePath?: string | null; dniDorsoPath?: string | null }>;
    documentos: Array<{ tipo: string; path: string; nombreOriginal: string; mimeType?: string; tamanio?: number }>;
  }) => api.post(`/reconocimiento/${id}/cargar-documentacion`, body),

  /**
   * Carga (por el apoderado, vía wizard) las autoridades DEFINITIVAS electas en
   * las internas + los apoderados + los tres documentos de la etapa y los datos
   * del partido que faltaban. Solo válido en fase AUTORIDADES_PENDIENTES.
   * Al enviarse, los promotores pasan a la nómina histórica (lo hace el backend).
   */
  cargarAutoridadesDefinitivas: (id: string, body: {
    organos: Array<{
      tipoOrgano: string;
      denominacion?: string;
      denominacionPersonalizada?: string | null;
      esObligatorio?: boolean;
      mandatoDesde?: string | null;
      mandatoHasta?: string | null;
      autoridades: Array<{ dni: string; nombre: string; apellido: string; genero?: string; cargo: string; esTitular: boolean; dniFrentePath?: string | null; dniDorsoPath?: string | null }>;
    }>;
    apoderados: Array<{ dni: string; nombre: string; apellido: string; genero?: string; dniFrentePath?: string | null; dniDorsoPath?: string | null }>;
    documentos: Array<{ tipo: string; path: string; nombreOriginal: string; mimeType?: string; tamanio?: number }>;
    partido?: {
      domicilioLegal?: string | null;
      // Casilla oficial del partido: es uno de los destinatarios reales de los
      // avisos del trámite (ver resolverDestinatariosPartido en el backend).
      emailOficial?: string | null;
      localidad?: string | null;
      codigoPostal?: string | null;
      telefonoOficial?: string | null;
      sitioWeb?: string | null;
      redesSociales?: string | null;
      fechaUltimaEleccionInterna?: string | null;
    };
  }) => api.post(`/reconocimiento/${id}/cargar-autoridades-definitivas`, body),

  /** Estado de los tres documentos de la etapa de autoridades definitivas. */
  estadoAutoridades: (id: string) => api.get(`/reconocimiento/${id}/estado-autoridades`),
};

// =============================================================================
// Interacciones con Partidos
// =============================================================================

export const interaccionService = {
  list: (params?: Record<string, string>) =>
    api.get('/interacciones', { params }).then(r => r.data),

  getById: (id: string) =>
    api.get(`/interacciones/${id}`).then(r => r.data),

  create: (data: Record<string, unknown>) =>
    api.post('/interacciones', data).then(r => r.data),

  update: (id: string, data: Record<string, unknown>) =>
    api.put(`/interacciones/${id}`, data).then(r => r.data),

  delete: (id: string) =>
    api.delete(`/interacciones/${id}`).then(r => r.data),

  uploadAdjunto: (formData: FormData) =>
    api.post('/interacciones/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data),
  
  getAdjuntoUrl: (path: string) =>
    api.get('/interacciones/adjunto/url', { params: { path } }).then(r => r.data),

  createNota: (id: string, texto: string) =>
    api.post(`/interacciones/${id}/notas`, { texto }).then(r => r.data),

  archivar: (id: string) =>
    api.patch(`/interacciones/${id}/archivar`).then(r => r.data),

  desarchivar: (id: string) =>
    api.patch(`/interacciones/${id}/desarchivar`).then(r => r.data),
};

// =============================================================================
// Consultas de partidos a la SEP (#388). Endpoints de la card #386.
// =============================================================================

export const consultaService = {
  list: (params?: Record<string, string>) =>
    api.get('/consultas', { params }).then(r => r.data),

  getById: (id: string) =>
    api.get(`/consultas/${id}`).then(r => r.data),

  /** #389: datos de la observación o intimación desde la que se inicia la consulta. */
  getReferencia: (expedienteId: string, observacionId: string) =>
    api.get('/consultas/referencia', { params: { expedienteId, observacionId } }).then(r => r.data),

  /** Si el usuario puede iniciar consultas (apoderado firmante o apoderado iniciador). */
  permisos: () =>
    api.get('/consultas/permisos').then(r => r.data),

  /** Contador del partido: consultas con novedades de la SEP que todavía no vio. */
  pendientes: () =>
    api.get('/consultas/pendientes').then(r => r.data),

  create: (formData: FormData) =>
    api.post('/consultas', formData, MULTIPART).then(r => r.data),

  enviarMensaje: (id: string, formData: FormData) =>
    api.post(`/consultas/${id}/mensajes`, formData, MULTIPART).then(r => r.data),

  getAdjuntoUrl: (id: string, adjuntoId: string) =>
    api.get(`/consultas/${id}/adjuntos/${adjuntoId}/url`).then(r => r.data),
};

// =============================================================================
// Consultas de partidos: circuito de la SEP (#390). Endpoints de la card #387.
// =============================================================================

export const consultaSepService = {
  list: (params?: Record<string, string>) =>
    api.get('/sep/consultas', { params }).then(r => r.data),

  getById: (id: string) =>
    api.get(`/sep/consultas/${id}`).then(r => r.data),

  asignables: () =>
    api.get('/sep/consultas/asignables').then(r => r.data),

  asignar: (id: string, usuarioId: string | null) =>
    api.patch(`/sep/consultas/${id}/asignacion`, { usuarioId }).then(r => r.data),

  /** FormData con texto, cerrar ('true' | 'false') y archivos. */
  responder: (id: string, formData: FormData) =>
    api.post(`/sep/consultas/${id}/respuestas`, formData, MULTIPART).then(r => r.data),

  crearNota: (id: string, texto: string) =>
    api.post(`/sep/consultas/${id}/notas`, { texto }).then(r => r.data),

  getAdjuntoUrl: (id: string, adjuntoId: string) =>
    api.get(`/sep/consultas/${id}/adjuntos/${adjuntoId}/url`).then(r => r.data),

  /** La SEP inicia una consulta: FormData con partidoId, asunto, texto, expedienteId (opcional) y archivos. */
  crear: (formData: FormData) =>
    api.post('/sep/consultas', formData, MULTIPART).then(r => r.data),

  /** Cierra sin responder. No se le avisa al partido. */
  cerrar: (id: string) =>
    api.patch(`/sep/consultas/${id}/cierre`).then(r => r.data),

  /** Cantidad de consultas que esperan respuesta de la SEP (contador del menú). */
  pendientes: () =>
    api.get('/sep/consultas/pendientes').then(r => r.data),
};

export interface CuentaBancariaPayload {
  cbu: string;
  banco: string;
  titular: string;
  sucursal?: string;
  fechaApertura: string;
}

export const cuentaBancariaService = {
  getByPartido: (partidoId: string) =>
    api.get(`/cuenta-bancaria/${partidoId}`).then(r => r.data),

  create: (partidoId: string, data: CuentaBancariaPayload) =>
    api.post(`/cuenta-bancaria/${partidoId}`, data).then(r => r.data),
};

// =============================================================================
// Rendición Anual (Financiamiento — Art. 53 Ley XII-23)
// =============================================================================

export type TipoPresentacionRendicion = 'COMPLETA' | 'COPIA_DISTRITO';
export type EstadoRendicion = 'BORRADOR' | 'PRESENTADA' | 'EN_REVISION' | 'APROBADA' | 'DESAPROBADA' | 'OBSERVADA';
export type TipoPersonaAportante = 'HUMANA' | 'JURIDICA';
export type ModalidadAporte = 'TRANSFERENCIA' | 'DEPOSITO' | 'ESPECIE';
export type SeccionFlujoEfectivo = 'OPERATIVA' | 'INVERSION' | 'FINANCIACION' | 'FINANCIERO_TENENCIA';
export type TipoFlujoLinea = 'COBRO' | 'PAGO';

export interface RendicionAnualPaso1 {
  tipoPresentacion: TipoPresentacionRendicion;
  ejercicioNumero: number;
  fechaInicio: string;
  fechaCierre: string;
  codigoSpeca?: string | null;
  versionSpeca?: number | null;
}

export interface AportePrivadoPayload {
  apellido: string;
  nombre?: string | null;
  razonSocial?: string | null;
  dni?: string | null;
  cuit: string;
  tipoPersona: TipoPersonaAportante;
  modalidad: ModalidadAporte;
  descripcionEspecie?: string | null;
  fecha: string;
  importe: number;
}

export interface SaldoPayload {
  codigoCuenta: string;
  debe: number;
  haber: number;
}

export interface FlujoLineaPayload {
  seccion: SeccionFlujoEfectivo;
  tipo: TipoFlujoLinea;
  concepto: string;
  importe: number;
  orden?: number;
}

export interface FlujoEfectivoPayload {
  efectivoInicio: number;
  efectivoCierre: number;
  lineas: FlujoLineaPayload[];
}

export const rendicionAnualService = {
  // Paso 1 + core
  listByPartido: (partidoId: string) =>
    api.get(`/rendiciones-anuales/partido/${partidoId}`).then(r => r.data),

  create: (partidoId: string, data: RendicionAnualPaso1) =>
    api.post(`/rendiciones-anuales/partido/${partidoId}`, data).then(r => r.data),

  getById: (id: string) =>
    api.get(`/rendiciones-anuales/${id}`).then(r => r.data),

  updatePaso1: (id: string, data: RendicionAnualPaso1) =>
    api.patch(`/rendiciones-anuales/${id}/paso1`, data).then(r => r.data),

  delete: (id: string) =>
    api.delete(`/rendiciones-anuales/${id}`).then(r => r.data),

  presentarCopiaDistrito: (id: string, data: { declaracionJurada: boolean }) =>
    api.post(`/rendiciones-anuales/${id}/presentar-copia-distrito`, data).then(r => r.data),

  generarVersionDistrito: (id: string, data: { documentoId: string }) =>
    api.post(`/rendiciones-anuales/${id}/version-distrito`, data).then(r => r.data),

  getReportes: (id: string) =>
    api.get(`/rendiciones-anuales/${id}/reportes`).then(r => r.data),

  getValidaciones: (id: string) =>
    api.get(`/rendiciones-anuales/${id}/validaciones`).then(r => r.data),

  // Paso 6 — versionado y presentación
  // Descargar PDF borrador (genera fila Version con esBorrador=true y devuelve el archivo)
  descargarPdfBorrador: (id: string) =>
    api.get(`/rendiciones-anuales/${id}/pdf-borrador`, { responseType: 'blob' }),

  // Cristaliza el borrador firmando con el PDF certificado (Documento)
  generarVersion: (id: string, data: { documentoId: string }) =>
    api.post(`/rendiciones-anuales/${id}/versiones`, data).then(r => r.data),

  listVersiones: (id: string) =>
    api.get(`/rendiciones-anuales/${id}/versiones`).then(r => r.data),

  descargarVersionPdf: (id: string, numeroVersion: number) =>
    api.get(`/rendiciones-anuales/${id}/versiones/${numeroVersion}/pdf`).then(r => r.data),

  getVersionSnapshot: (id: string, numeroVersion: number) =>
    api.get(`/rendiciones-anuales/${id}/versiones/${numeroVersion}/snapshot`).then(r => r.data),

  presentar: (id: string, data: { declaracionJurada: boolean }) =>
    api.post(`/rendiciones-anuales/${id}/presentar`, data).then(r => r.data),

  // Plan de cuentas (referencia)
  getPlanCuentas: () =>
    api.get('/rendiciones-anuales/plan-cuentas').then(r => r.data),

  // Paso 2 — aportes
  listAportes: (rendicionId: string) =>
    api.get(`/rendiciones-anuales/${rendicionId}/aportes`).then(r => r.data),

  createAporte: (rendicionId: string, data: AportePrivadoPayload) =>
    api.post(`/rendiciones-anuales/${rendicionId}/aportes`, data).then(r => r.data),

  createAportesBulk: (rendicionId: string, aportes: AportePrivadoPayload[]) =>
    api.post(`/rendiciones-anuales/${rendicionId}/aportes/bulk`, { aportes }).then(r => r.data),

  updateAporte: (rendicionId: string, aporteId: string, data: AportePrivadoPayload) =>
    api.put(`/rendiciones-anuales/${rendicionId}/aportes/${aporteId}`, data).then(r => r.data),

  deleteAporte: (rendicionId: string, aporteId: string) =>
    api.delete(`/rendiciones-anuales/${rendicionId}/aportes/${aporteId}`).then(r => r.data),

  // Paso 3 — saldos
  listSaldos: (rendicionId: string) =>
    api.get(`/rendiciones-anuales/${rendicionId}/saldos`).then(r => r.data),

  saveSaldos: (rendicionId: string, saldos: SaldoPayload[]) =>
    api.post(`/rendiciones-anuales/${rendicionId}/saldos/bulk`, { saldos }).then(r => r.data),

  // Paso 4 — flujo de efectivo
  getFlujo: (rendicionId: string) =>
    api.get(`/rendiciones-anuales/${rendicionId}/flujo-efectivo`).then(r => r.data),

  saveFlujo: (rendicionId: string, data: FlujoEfectivoPayload) =>
    api.put(`/rendiciones-anuales/${rendicionId}/flujo-efectivo`, data).then(r => r.data),
};

// =============================================================================
// Rendición de Campaña Electoral (Financiamiento — Art. 53 inc. c, Ley XII-23)
// =============================================================================

export type TipoEleccion =
  | 'LEGISLATIVA_PROVINCIAL'
  | 'EJECUTIVA_PROVINCIAL'
  | 'MUNICIPAL'
  | 'CONSEJO_MAGISTRATURA';

export type EtapaEleccion = 'PRIMARIA' | 'GENERAL' | 'SEGUNDA_VUELTA';

export type TipoMovimientoCampana = 'INGRESO' | 'EGRESO';

export interface RendicionCampanaPaso1 {
  tipoPresentacion: TipoPresentacionRendicion;
  tipoEleccion: TipoEleccion;
  etapa: EtapaEleccion;
  fechaActoElectoral: string;
  codigoSpeca?: string | null;
  versionSpeca?: number | null;
}

export interface CandidaturaPayload {
  categoria: string;
  detalle?: string | null;
  orden?: number;
}

export interface MovimientoCampanaPayload {
  codigoCuenta: string;
  importe: number;
}

export const rendicionCampanaService = {
  // Paso 1 + core
  listByPartido: (partidoId: string) =>
    api.get(`/rendiciones-campana/partido/${partidoId}`).then(r => r.data),

  create: (partidoId: string, data: RendicionCampanaPaso1) =>
    api.post(`/rendiciones-campana/partido/${partidoId}`, data).then(r => r.data),

  getById: (id: string) =>
    api.get(`/rendiciones-campana/${id}`).then(r => r.data),

  updatePaso1: (id: string, data: RendicionCampanaPaso1) =>
    api.patch(`/rendiciones-campana/${id}/paso1`, data).then(r => r.data),

  delete: (id: string) =>
    api.delete(`/rendiciones-campana/${id}`).then(r => r.data),

  presentarCopiaDistrito: (id: string, data: { declaracionJurada: boolean }) =>
    api.post(`/rendiciones-campana/${id}/presentar-copia-distrito`, data).then(r => r.data),

  generarVersionDistrito: (id: string, data: { documentoId: string }) =>
    api.post(`/rendiciones-campana/${id}/version-distrito`, data).then(r => r.data),

  getReportes: (id: string) =>
    api.get(`/rendiciones-campana/${id}/reportes`).then(r => r.data),

  getValidaciones: (id: string) =>
    api.get(`/rendiciones-campana/${id}/validaciones`).then(r => r.data),

  // Paso 6 — versionado y presentación
  descargarPdfBorrador: (id: string) =>
    api.get(`/rendiciones-campana/${id}/pdf-borrador`, { responseType: 'blob' }),

  generarVersion: (id: string, data: { documentoId: string }) =>
    api.post(`/rendiciones-campana/${id}/versiones`, data).then(r => r.data),

  listVersiones: (id: string) =>
    api.get(`/rendiciones-campana/${id}/versiones`).then(r => r.data),

  descargarVersionPdf: (id: string, numeroVersion: number) =>
    api.get(`/rendiciones-campana/${id}/versiones/${numeroVersion}/pdf`).then(r => r.data),

  getVersionSnapshot: (id: string, numeroVersion: number) =>
    api.get(`/rendiciones-campana/${id}/versiones/${numeroVersion}/snapshot`).then(r => r.data),

  presentar: (id: string, data: { declaracionJurada: boolean }) =>
    api.post(`/rendiciones-campana/${id}/presentar`, data).then(r => r.data),

  // Plan de cuentas filtrado para campaña local
  getPlanCuentas: () =>
    api.get('/rendiciones-campana/plan-cuentas').then(r => r.data),

  // Paso 1 — candidaturas
  listCandidaturas: (rendicionId: string) =>
    api.get(`/rendiciones-campana/${rendicionId}/candidaturas`).then(r => r.data),

  createCandidatura: (rendicionId: string, data: CandidaturaPayload) =>
    api.post(`/rendiciones-campana/${rendicionId}/candidaturas`, data).then(r => r.data),

  updateCandidatura: (rendicionId: string, candidaturaId: string, data: CandidaturaPayload) =>
    api.put(`/rendiciones-campana/${rendicionId}/candidaturas/${candidaturaId}`, data).then(r => r.data),

  deleteCandidatura: (rendicionId: string, candidaturaId: string) =>
    api.delete(`/rendiciones-campana/${rendicionId}/candidaturas/${candidaturaId}`).then(r => r.data),

  // Paso 2 — aportes
  listAportes: (rendicionId: string) =>
    api.get(`/rendiciones-campana/${rendicionId}/aportes`).then(r => r.data),

  createAporte: (rendicionId: string, data: AportePrivadoPayload) =>
    api.post(`/rendiciones-campana/${rendicionId}/aportes`, data).then(r => r.data),

  createAportesBulk: (rendicionId: string, aportes: AportePrivadoPayload[]) =>
    api.post(`/rendiciones-campana/${rendicionId}/aportes/bulk`, { aportes }).then(r => r.data),

  updateAporte: (rendicionId: string, aporteId: string, data: AportePrivadoPayload) =>
    api.put(`/rendiciones-campana/${rendicionId}/aportes/${aporteId}`, data).then(r => r.data),

  deleteAporte: (rendicionId: string, aporteId: string) =>
    api.delete(`/rendiciones-campana/${rendicionId}/aportes/${aporteId}`).then(r => r.data),

  // Pasos 3 y 4 — movimientos
  listMovimientos: (rendicionId: string) =>
    api.get(`/rendiciones-campana/${rendicionId}/movimientos`).then(r => r.data),

  saveMovimientos: (rendicionId: string, movimientos: MovimientoCampanaPayload[]) =>
    api.post(`/rendiciones-campana/${rendicionId}/movimientos/bulk`, { movimientos }).then(r => r.data),

  getResumen: (rendicionId: string) =>
    api.get(`/rendiciones-campana/${rendicionId}/resumen`).then(r => r.data),
};

// =============================================================================
// Gestión INTERNA de expedientes de alianza (Card D / U3)
// =============================================================================
// Contraparte de routes/alianzasGestion.ts. Va aparte de `alianzaService` porque
// ese es el trámite PÚBLICO (sin sesión) y esto es el lado autenticado.
export interface FiltrosBandejaAlianzas {
  convocatoriaId?: string;
  estado?: string;
  analistaId?: string;
  busqueda?: string;
  pagina?: number;
  porPagina?: number;
}

/**
 * Una alianza como la ve el partido desde su portal externo. Contraparte de
 * `AlianzaDelPartido` en services/alianzaGestionService del backend.
 */
export interface AlianzaDelPartido {
  alianzaId: string;
  denominacion: string;
  sigla: string | null;
  numeroAlianza: number | null;
  estadoAlianza: string;
  /**
   * `true` = el usuario tiene acceso a la alianza y puede abrir el expediente.
   * `false` = su partido sólo la integra: ve el banner del inicio y recibe los
   * correos, pero el expediente le devuelve 403.
   */
  tieneAcceso: boolean;
  convocatoria: {
    id: string;
    denominacion: string;
    codigoCorto: string | null;
    fechaActo: string | null;
  } | null;
  expedienteId: string;
  expedienteNumero: string;
  estadoExpediente: string;
  observacionesPendientes: number;
  fechaLimiteRespuesta: string | null;
  integrantes: string[];
  resolucion: {
    numero: string | null;
    fecha: string | null;
    reconoce: boolean;
    archivos: Array<{ id: string; nombre: string }>;
  } | null;
}

export const alianzaGestionService = {
  // Portal externo: las alianzas del partido del usuario logueado (las que integra
  // y la que ES). Alimenta el banner del /dashboard.
  misAlianzas: (): Promise<AlianzaDelPartido[]> =>
    api.get('/alianzas/mis-alianzas').then(r => r.data.data),

  bandeja: (filtros: FiltrosBandejaAlianzas = {}) =>
    api.get('/alianzas/expedientes', { params: filtros }).then(r => r.data.data),

  contadores: (convocatoriaId?: string) =>
    api.get('/alianzas/expedientes/contadores', {
      params: convocatoriaId ? { convocatoriaId } : undefined,
    }).then(r => r.data.data),

  analistas: () =>
    api.get('/alianzas/expedientes/analistas').then(r => r.data.data),

  detalle: (expedienteId: string) =>
    api.get(`/alianzas/expedientes/${expedienteId}`).then(r => r.data.data),

  timeline: (expedienteId: string) =>
    api.get(`/alianzas/expedientes/${expedienteId}/timeline`).then(r => r.data.data),

  asignar: (expedienteId: string, analistaId: string) =>
    api.post(`/alianzas/expedientes/${expedienteId}/asignar`, { analistaId }),

  iniciarAnalisis: (expedienteId: string) =>
    api.post(`/alianzas/expedientes/${expedienteId}/iniciar-analisis`),

  verificarIntegrante: (expedienteId: string, integranteId: string) =>
    api.post(`/alianzas/expedientes/${expedienteId}/integrantes/${integranteId}/verificar`)
      .then(r => r.data.data),

  observar: (expedienteId: string, formData: FormData) =>
    api.post(`/alianzas/expedientes/${expedienteId}/observaciones`, formData, { ...MULTIPART, timeout: 300000 }),

  responderObservacion: (observacionId: string, texto: string, documentoIds: string[] = []) =>
    api.post(`/alianzas/observaciones/${observacionId}/responder`, { texto, documentoIds }),

  borradorInforme: (expedienteId: string) =>
    api.get(`/alianzas/expedientes/${expedienteId}/informe-tecnico/borrador`).then(r => r.data.data),

  // SIN `registrarResolucion`: el Secretario ya no dicta la resolución de la
  // alianza (POST /alianzas/expedientes/:id/resolucion no existe más en el
  // backend). La elevación al TEP y las tres salidas del acta se disparan desde
  // ModalInformeSimple y ModalResolucionTep, que arman la URL con `basePath`.
};

export type EstadoVerificacion =
  | 'ABIERTA' | 'EN_COTEJO' | 'EN_EXAMEN_PERICIAL' | 'EN_SORTEO'
  | 'EN_VERIFICACION' | 'EN_SUBSANACION' | 'CERRADA' | 'ANULADA';
export type ResultadoCotejo = 'APROBADO' | 'DESCARTADO' | 'PENDIENTE';
export type MotivoCotejo =
  | 'DATOS_NO_CORRESPONDEN' | 'NO_INSCRIPTO_EN_DISTRITO' | 'DUPLICADA_O_SIMULTANEA'
  | 'PROHIBICION_ART_29' | 'FICHA_FORMALMENTE_INCOMPLETA' | 'FALLECIDO_A_LA_SUSCRIPCION';

interface PartidoVerificacion {
  id: string;
  denominacion: string;
  sigla: string | null;
}

export interface VerificacionResumida {
  id: string;
  supuesto: string;
  estado: EstadoVerificacion;
  fechaApertura: string;
  expediente: { id: string; numero: string; partido: PartidoVerificacion };
}

export interface VerificacionDetalle extends Omit<VerificacionResumida, 'expediente'> {
  minimoLegal: number;
  rondaActual: number;
  fechaRemisionPeritos: string | null;
  fechaInformePericial: string | null;
  informePericialDocumento: { id: string; nombreOriginal: string } | null;
  expediente: { id: string; numero: string; estado: string; partido: PartidoVerificacion };
}

export interface ResumenCotejo {
  presentadas: number;
  aprobadas: number;
  descartadas: number;
  pendientes: number;
  descartadasPorMotivo: Partial<Record<MotivoCotejo, number>>;
  minimoLegal: number;
  senaladas: number;
}

export interface UnidadCotejo {
  id: string;
  numeroFicha: number;
  dni: string;
  apellido: string;
  nombre: string;
  resultadoCotejo: ResultadoCotejo;
  motivosCotejo: MotivoCotejo[];
  domicilioFicha: string | null;
  domicilioPadron: string | null;
  domicilioNoCoincide: boolean;
  origenMuestra: string;
  indicadorPericial: string | null;
  cotejoRevisadoEn: string | null;
}

export interface FichaSenalada {
  unidadId: string;
  indicador?: string | null;
}

export const verificacionAutenticidadService = {
  listar: (partidoId?: string): Promise<VerificacionResumida[]> =>
    api.get('/verificacion-autenticidad', { params: partidoId ? { partidoId } : undefined }).then(r => r.data.data),

  detalle: (id: string): Promise<VerificacionDetalle> =>
    api.get(`/verificacion-autenticidad/${id}`).then(r => r.data.data),

  resumenCotejo: (id: string): Promise<ResumenCotejo> =>
    api.get(`/verificacion-autenticidad/${id}/resumen-cotejo`).then(r => r.data.data),

  unidades: (id: string, resultado?: ResultadoCotejo): Promise<UnidadCotejo[]> =>
    api.get(`/verificacion-autenticidad/${id}/unidades`, { params: resultado ? { resultado } : undefined }).then(r => r.data.data),

  ejecutarCotejo: (id: string) =>
    api.post(`/verificacion-autenticidad/${id}/cotejo`).then(r => r.data.data),
  resolverPendientesDomicilio: (id: string, unidadIds: string[], decision: 'APROBAR' | 'RECHAZAR') =>
    api.post(`/verificacion-autenticidad/${id}/pendientes-domicilio`, { unidadIds, decision }).then(r => r.data.data),

  remitirAPeritos: (id: string, fechaRemision: string) =>
    api.post(`/verificacion-autenticidad/${id}/remitir-peritos`, { fechaRemision }).then(r => r.data.data),

  registrarInformePericial: (id: string, archivo: File, fechaInforme: string, senaladas: FichaSenalada[]) => {
    const form = new FormData();
    form.append('archivo', archivo);
    form.append('fechaInforme', fechaInforme);
    form.append('senaladas', JSON.stringify(senaladas));
    return api.post(`/verificacion-autenticidad/${id}/informe-pericial`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data.data);
  },

  continuarAlSorteo: (id: string) =>
    api.post(`/verificacion-autenticidad/${id}/continuar-sorteo`),
};

// =============================================================================
// Lotes de afiliación (Art. 32 Ley XII-23)
// =============================================================================

export type EstadoLotePartido = 'BORRADOR' | 'EN_ESPERA_SEP' | 'EN_SUBSANACION' | 'RESUELTO' | 'DESCARTADO';

export interface ResumenLote {
  total: number;
  sinObservaciones?: number;
  conObservaciones?: number;
  requierenConstancia?: number;
}

export type EstadoFichaLote = 'PENDIENTE' | 'APROBADA' | 'RECHAZADA';

export interface FichaLote {
  id: string;
  dni: string;
  apellido: string;
  nombre: string;
  fechaAfiliacion: string | null;
  fechaNacimiento: string | null;
  mensajes: string[];
  conObservaciones: boolean;
  admiteConstancia: boolean;
  constanciaAdjunta: boolean;
  constanciaNombre: string | null;
  resultado: { estado: EstadoFichaLote; causaRechazo: string | null } | null;
}

export interface LoteAfiliacion {
  id: string;
  partidoId: string;
  numero: string;
  estado: EstadoLotePartido;
  archivoNombre: string | null;
  fechaPresentacion: string | null;
  fechaRecepcionFichas: string | null;
  creadoEn: string;
  resumen: ResumenLote;
  fichas: FichaLote[];
}

export type LoteAfiliacionResumido = Omit<LoteAfiliacion, 'partidoId' | 'archivoNombre' | 'fichas'> & { descartadoEn: string | null };

export interface FilaRechazadaLote {
  dni: string | null;
  apellido: string | null;
  nombre: string | null;
  motivos: string[];
}

export interface ResultadoCargaExcel {
  cargadas: number;
  rechazadas: FilaRechazadaLote[];
  lote: LoteAfiliacion;
}

export interface AltaFichaPayload {
  dni: string;
  apellido: string;
  nombre: string;
  fechaNacimiento: string;
  fechaAfiliacion: string;
}

export interface EdicionFichaPayload {
  dni?: string;
  apellido?: string;
  nombre?: string;
  fechaAfiliacion?: string;
  fechaNacimiento?: string;
}

export const loteAfiliacionService = {
  listar: (partidoId: string): Promise<LoteAfiliacionResumido[]> =>
    api.get(`/lotes-afiliacion/partido/${partidoId}`).then(r => r.data.data),

  crear: (partidoId: string): Promise<LoteAfiliacion> =>
    api.post(`/lotes-afiliacion/partido/${partidoId}`).then(r => r.data.data),

  detalle: (loteId: string): Promise<LoteAfiliacion> =>
    api.get(`/lotes-afiliacion/${loteId}`).then(r => r.data.data),

  cargarExcel: (loteId: string, archivo: File): Promise<ResultadoCargaExcel> => {
    const form = new FormData();
    form.append('archivo', archivo);
    return api.post(`/lotes-afiliacion/${loteId}/excel`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data.data);
  },

  agregarFicha: (loteId: string, datos: AltaFichaPayload): Promise<LoteAfiliacion> =>
    api.post(`/lotes-afiliacion/${loteId}/fichas`, datos).then(r => r.data.data),

  editarFicha: (fichaId: string, cambios: EdicionFichaPayload): Promise<LoteAfiliacion> =>
    api.patch(`/lotes-afiliacion/fichas/${fichaId}`, cambios).then(r => r.data.data),

  quitarFicha: (fichaId: string): Promise<LoteAfiliacion> =>
    api.delete(`/lotes-afiliacion/fichas/${fichaId}`).then(r => r.data.data),

  adjuntarConstancia: (fichaId: string, archivo: File): Promise<LoteAfiliacion> => {
    const form = new FormData();
    form.append('archivo', archivo);
    return api.post(`/lotes-afiliacion/fichas/${fichaId}/constancia`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data.data);
  },

  presentar: (loteId: string): Promise<LoteAfiliacion> =>
    api.post(`/lotes-afiliacion/${loteId}/presentar`).then(r => r.data.data),
  descartar: (loteId: string) =>
    api.delete(`/lotes-afiliacion/${loteId}`).then(r => r.data.data),
};

export type EstadoLoteSep = 'PRESENTADO' | 'FICHAS_RECIBIDAS' | 'EN_VERIFICACION' | 'EN_SUBSANACION' | 'RESUELTO';

export interface LoteSepResumido {
  id: string;
  numero: string;
  estado: EstadoLoteSep;
  fechaPresentacion: string | null;
  fechaRecepcionFichas: string | null;
  partido: { id: string; denominacion: string; sigla: string | null };
  expediente: { id: string; numero: string } | null;
  totalFichas: number;
  fichasRecibidas: number;
}

export interface FichaSep {
  id: string;
  dni: string;
  apellido: string;
  nombre: string;
  fechaAfiliacion: string | null;
  fechaNacimiento: string | null;
  resultadoControl: string;
  motivosControl: string[];
  defectosFicha: string[];
  mensajesPartido: string[];
  detalleInterno: string | null;
  constanciaNombre: string | null;
  fichaRecibida: boolean;
  numeroFicha: number | null;
  conObservaciones: boolean;
}

export interface LoteSepDetalle extends Omit<LoteSepResumido, 'expediente' | 'totalFichas' | 'fichasRecibidas'> {
  archivoNombre: string | null;
  partido: { id: string; denominacion: string; sigla: string | null; tipo: string };
  expediente: { id: string; numero: string; estado: string } | null;
  solicitudes: FichaSep[];
  verificacion: { id: string; supuesto: string; estado: EstadoVerificacion } | null;
  supuestoPrevisto: string | null;
}

export const loteAfiliacionSepService = {
  listar: (): Promise<LoteSepResumido[]> =>
    api.get('/lotes-afiliacion/sep').then(r => r.data.data),
  detalle: (loteId: string): Promise<LoteSepDetalle> =>
    api.get(`/lotes-afiliacion/sep/${loteId}`).then(r => r.data.data),
  registrarRecepcion: (loteId: string, recibidas: string[]): Promise<LoteSepDetalle> =>
    api.post(`/lotes-afiliacion/sep/${loteId}/recepcion`, { recibidas }).then(r => r.data.data),
  abrirVerificacion: (loteId: string): Promise<LoteSepDetalle> =>
    api.post(`/lotes-afiliacion/sep/${loteId}/verificacion`).then(r => r.data.data),
};

export default api;
